import React from 'react';
import { Button, ModalFooter, ModalHeader, type ModalProps } from 'reactstrap';
import DialogModal from './DialogModal';

// TypeScript declaration for build-time process.env substitution
declare const process: {
    env: {
        NODE_ENV: string;
    };
};

// Strip reactstrap's `[key: string]: any` catch-all so destructured props keep their
// narrow types instead of `any`. `title` is Omit'd because HTMLAttributes types it as
// string; `toggle` because ModalProps' union includes KeyboardEventHandler (not
// assignable to Button onClick / ModalHeader toggle).
type StrictModalProps = { [K in keyof ModalProps as string extends K ? never : K]: ModalProps[K] };

export type CancellableModalProps = Omit<StrictModalProps, 'title' | 'toggle' | 'labelledBy'> & {
    /** Id of the title element that names the dialog; see `DialogModal`. */
    labelledBy: string;
    /**
     * Required. Fires on backdrop click, Escape key, and header X — and on the injected
     * Cancel button too unless `onCancel` overrides it. **For one handler across all
     * dismissal paths, set only `toggle`.** Dev-throw enforces presence.
     *
     * The event arg can be a MouseEvent (backdrop click, header X) or a KeyboardEvent
     * (reactstrap's Escape handler in Modal.js:256 calls `toggle(e)` with the keyup event).
     * Callers that inspect the event must discriminate, e.g. `if ('button' in e)`.
     */
    toggle: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => unknown;
    /**
     * Optional override for the injected Cancel button **only**. Set this when you want
     * Cancel to differ from the other dismissal paths (e.g. log "rejected" vs "abandoned"
     * analytics, or prompt for discard-confirmation only on explicit Cancel). Omit when
     * Cancel should behave the same as backdrop / Escape / header X — it'll fall back to
     * `toggle` automatically.
     */
    onCancel?: () => unknown;
    /** Label on the injected Cancel button; also used for the dev-mode "already has Cancel" check. */
    cancelText?: string;
    /**
     * Autofocus the injected Cancel button on open. Used by destructive confirms (e.g.
     * `ConfirmModal` with `confirmColor === 'danger'`) to point the safer choice as the
     * initial focus per the WAI-ARIA dialog pattern. Default false.
     */
    autoFocusCancel?: boolean;
    /** Disables Cancel, header X, backdrop-click close, and Escape while an action is in flight. */
    busy?: boolean;
    /** Ref to the injected Cancel button (e.g. to restore focus to it programmatically). */
    cancelButtonRef?: React.Ref<HTMLButtonElement>;
};

// Module-scoped — reactstrap inserts the `close` override as-is (no cloneElement),
// so sharing this single element across every busy modal is safe.
const disabledCloseButton = <button type="button" className="btn-close" disabled aria-label="Close" />;

// Dev-mode check for mid-migration leftovers like `<Button>Cancel</Button>` inside a
// caller's <ModalFooter>. Narrow on purpose — bare-string children only; widening would
// need subtree walks or render inspection. Catches the obvious copy/paste, not every variant.
function footerHasCancel(footer: React.ReactElement, cancelText: string): boolean {
    const kids = React.Children.toArray((footer.props as { children?: React.ReactNode }).children);
    return kids.some((k) => {
        if (!React.isValidElement(k) || k.type !== Button) return false;
        const btnChildren = (k.props as { children?: React.ReactNode }).children;
        return typeof btnChildren === 'string' && btnChildren.trim() === cancelText;
    });
}

export default function CancellableModal({
    onCancel,
    cancelText = 'Cancel',
    autoFocusCancel,
    busy,
    cancelButtonRef,
    isOpen,
    toggle,
    backdrop,
    keyboard,
    children,
    ...modalProps
}: CancellableModalProps) {
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typed required, but JS / @ts-expect-error callers can still pass undefined
        if (!toggle) {
            throw new Error('CancellableModal requires a `toggle` prop — without it every close path is inert.');
        }
        const kids = React.Children.toArray(children);
        if (!kids.some((c) => React.isValidElement(c) && c.type === ModalHeader)) {
            throw new Error('CancellableModal requires a direct <ModalHeader> child (fragments are not unwrapped).');
        }
        if (!kids.some((c) => React.isValidElement(c) && c.type === ModalFooter)) {
            throw new Error('CancellableModal requires a direct <ModalFooter> child (fragments are not unwrapped).');
        }
        for (const c of kids) {
            if (!React.isValidElement(c)) continue;
            if (c.type === ModalFooter && footerHasCancel(c, cancelText)) {
                throw new Error(
                    `CancellableModal: <ModalFooter> already contains a "${cancelText}" button. ` +
                        'Remove it — CancellableModal injects one automatically.'
                );
            }
            if (c.type === ModalHeader) {
                const headerProps = c.props as { toggle?: unknown; close?: unknown };
                if (headerProps.toggle !== undefined || headerProps.close !== undefined) {
                    throw new Error(
                        'CancellableModal: <ModalHeader> must not set `toggle` or `close` — ' +
                            'the wrapper owns both so it can disable the X when busy.'
                    );
                }
            }
        }
    }

    const handleCancel = onCancel ?? toggle;
    const cancelBtn = (
        <Button
            key="cancellable-modal-cancel"
            innerRef={cancelButtonRef}
            onClick={handleCancel}
            disabled={busy}
            autoFocus={autoFocusCancel}
        >
            {cancelText}
        </Button>
    );

    // Clone <ModalHeader> overriding toggle/close (so the wrapper can disable the X when busy).
    // Clone <ModalFooter> with Cancel injected as the first child.
    const augmented = React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        if (child.type === ModalHeader) {
            return React.cloneElement(child as React.ReactElement<React.ComponentProps<typeof ModalHeader>>, {
                toggle,
                close: busy ? disabledCloseButton : undefined,
            });
        }
        if (child.type !== ModalFooter) return child;
        const footerChildren = React.Children.toArray((child.props as { children?: React.ReactNode }).children);
        return React.cloneElement(child, {}, cancelBtn, ...footerChildren);
    });

    // When busy: backdrop='static' blocks backdrop-click close, keyboard=false blocks Escape,
    // the injected Cancel and cloned X are both disabled. Reactstrap's native focus-scoped
    // Escape handles the non-busy case — we no longer install our own document listener.
    return (
        <DialogModal
            {...modalProps}
            isOpen={isOpen}
            toggle={toggle}
            backdrop={busy ? 'static' : backdrop}
            keyboard={busy ? false : keyboard}
        >
            {augmented}
        </DialogModal>
    );
}
